/** Faculty year records (`iabf-faculty-{year}`) hydrate like enrollments. */

import { festivalWeek } from '@/config/iabf-festival-dates'
import { IABF_DANCER_YEAR } from '@/config/iabf-event'

export function isFacultyAccount (person) {
  return !!(person && (person.isFaculty || person.personType === 'faculty'))
}

/**
 * Dashboard Resources + Coming soon tiles faculty see for 2027.
 * New dancer-facing tiles stay hidden unless their id is added here.
 */
export const FACULTY_GUIDE_IDS = ['lineup', 'itinerary', 'lessons', 'merch']

export function facultyAsDancer (rec, year) {
  const d = rec || {}
  return {
    ...d,
    isFaculty: true,
    personType: 'faculty',
    festivalYear: Number(d.festivalYear) || Number(year) || d.festivalYear
  }
}

/** Same stay types as dancer admin, plus empty (no bed). */
export function facultyMoneyNum (v) {
  if (v === '' || v === null || v === undefined) {
    return 0
  }
  const n = Number(v)
  if (!Number.isFinite(n) || n < 0) {
    return 0
  }
  return Math.round(n * 100) / 100
}

/** Inclusive count for community intro (“4th year at IABF”). 0 = not set. */
export function facultyYearsAtIabfNum (v) {
  if (v === '' || v === null || v === undefined) {
    return 0
  }
  const n = Math.round(Number(v))
  if (!Number.isFinite(n) || n < 0) {
    return 0
  }
  return Math.min(40, n)
}

export const FACULTY_ROOM_ITEMS = [
  { text: 'No onsite room', value: '' },
  { text: 'Standard Dorm', value: 'Standard Dorm' },
  { text: 'Larger Dorm', value: 'ADA Dorm' },
  { text: 'Cottage', value: 'Cottage' },
  { text: 'Staying with another dancer', value: 'Staying with another dancer' },
  { text: 'Staying offsite', value: 'Staying offsite' }
]

function addDaysIso (iso, days) {
  const d = new Date(String(iso) + 'T12:00:00')
  d.setDate(d.getDate() + days)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return y + '-' + m + '-' + day
}

/** Festival meals relative to Monday arrival. */
const FACULTY_MEAL_DEFS = [
  { key: 'welcome_dinner', offset: 0, slot: 'dinner' },
  { key: 'tue_breakfast', offset: 1, slot: 'breakfast' },
  { key: 'tue_lunch', offset: 1, slot: 'lunch' },
  { key: 'tue_dinner', offset: 1, slot: 'dinner' },
  { key: 'wed_breakfast', offset: 2, slot: 'breakfast' },
  { key: 'wed_lunch', offset: 2, slot: 'lunch' },
  { key: 'wed_dinner', offset: 2, slot: 'dinner' },
  { key: 'thu_breakfast', offset: 3, slot: 'breakfast' },
  { key: 'thu_lunch', offset: 3, slot: 'lunch' },
  { key: 'thu_dinner', offset: 3, slot: 'dinner' },
  { key: 'fri_breakfast', offset: 4, slot: 'breakfast' },
  { key: 'fri_lunch', offset: 4, slot: 'lunch' },
  { key: 'fri_banquet', offset: 4, slot: 'dinner' },
  { key: 'sat_breakfast', offset: 5, slot: 'breakfast' }
]

const MEAL_ABBREV = {
  welcome_dinner: 'Welcome Dinner',
  tue_breakfast: 'Tue B',
  tue_lunch: 'Tue L',
  tue_dinner: 'Tue D',
  wed_breakfast: 'Wed B',
  wed_lunch: 'Wed L',
  wed_dinner: 'Wed D',
  thu_breakfast: 'Thu B',
  thu_lunch: 'Thu L',
  thu_dinner: 'Thu D',
  fri_breakfast: 'Fri B',
  fri_lunch: 'Fri L',
  fri_banquet: 'Fri Banquet',
  sat_breakfast: 'Sat B'
}

export function facultyStayDates (faculty, year = IABF_DANCER_YEAR) {
  const week = festivalWeek(year)
  let arrival = String((faculty && faculty.arrivalDate) || '').trim() || week.arrivalIso
  let departure = String((faculty && faculty.departureDate) || '').trim() || week.departureIso
  if (departure < arrival) {
    departure = arrival
  }
  return { arrival, departure, defaultArrival: week.arrivalIso, defaultDeparture: week.departureIso }
}

function mealOnStay (slot, mealDate, arrival, departure) {
  if (slot === 'breakfast') {
    return mealDate > arrival && mealDate <= departure
  }
  if (slot === 'lunch') {
    return mealDate > arrival && mealDate < departure
  }
  return mealDate >= arrival && mealDate < departure
}

/** Meal keys a faculty member attends from their arrival / departure dates. */
export function facultyMealsFromStay (faculty, year = IABF_DANCER_YEAR) {
  const stay = facultyStayDates(faculty, year)
  const mon = festivalWeek(year).arrivalIso
  return FACULTY_MEAL_DEFS
    .filter((def) => mealOnStay(def.slot, addDaysIso(mon, def.offset), stay.arrival, stay.departure))
    .map((def) => def.key)
}

export function facultyMealsLabel (faculty, year = IABF_DANCER_YEAR) {
  const meals = facultyMealsFromStay(faculty, year)
  if (meals.length === FACULTY_MEAL_DEFS.length) {
    return 'All Meals'
  }
  if (!meals.length) {
    return 'None'
  }
  return meals.map((k) => MEAL_ABBREV[k] || k).join(', ')
}

export function formatFacultyStayDate (iso) {
  if (!iso) {
    return ''
  }
  const d = new Date(String(iso) + 'T12:00:00')
  if (Number.isNaN(d.getTime())) {
    return String(iso)
  }
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}
